import * as React from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Button,
  TextField,
  Typography,
} from "@mui/material";

/* -------- Types -------- */
export type DateRange = { start: Date; end: Date };
export type Preset = "TODAY" | "P7" | "PMONTH" | "PYEAR" | "CUSTOM";

/* -------- Helpers (shared) -------- */
export const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
export const endOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};
export const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
export const ymd = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(x.getDate()).padStart(2, "0")}`;
};

export const getRangeForPreset = (p: Preset): DateRange => {
  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);
  switch (p) {
    case "TODAY":
      return { start: todayStart, end: todayEnd };
    case "P7":
      return { start: startOfDay(addDays(now, -6)), end: todayEnd };
    case "PMONTH":
      return { start: startOfDay(addDays(now, -29)), end: todayEnd };
    case "PYEAR":
      return { start: startOfDay(addDays(now, -364)), end: todayEnd };
    default:
      return { start: todayStart, end: todayEnd };
  }
};

type DateRangeControlsProps = {
  value: DateRange;
  onChange: (range: DateRange) => void;
  defaultPreset?: Preset;
};

export default function DateRangeControls({
  value,
  onChange,
  defaultPreset = "P7",
}: DateRangeControlsProps) {
  const [preset, setPreset] = React.useState<Preset>(defaultPreset);
  const [customOpen, setCustomOpen] = React.useState(false);
  const [customStart, setCustomStart] = React.useState<string>(
    ymd(value.start)
  );
  const [customEnd, setCustomEnd] = React.useState<string>(ymd(value.end));

  const handlePreset = (p: Preset) => {
    if (p === "CUSTOM") {
      setCustomStart(ymd(value.start));
      setCustomEnd(ymd(value.end));
      setCustomOpen(true);
      setPreset("CUSTOM");
      return;
    }
    setPreset(p);
    onChange(getRangeForPreset(p));
  };

  const applyCustom = () => {
    const s = new Date(customStart);
    const e = new Date(customEnd);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) {
      setCustomOpen(false);
      return;
    }
    const start = s.getTime() <= e.getTime() ? startOfDay(s) : startOfDay(e);
    const end = s.getTime() <= e.getTime() ? endOfDay(e) : endOfDay(s);
    onChange({ start, end });
    setCustomOpen(false);
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 3,
          background: "transparent",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          {value.start.toLocaleDateString()} – {value.end.toLocaleDateString()}
        </Typography>

        {(["TODAY", "P7", "PMONTH", "PYEAR"] as Preset[]).map((p) => (
          <Chip
            key={p}
            label={
              p === "TODAY"
                ? "Today"
                : p === "P7"
                ? "Past 7 days"
                : p === "PMONTH"
                ? "Past month"
                : "Past year"
            }
            color={preset === p ? "primary" : "default"}
            variant={preset === p ? "filled" : "outlined"}
            onClick={() => handlePreset(p)}
            sx={{
              ...(preset !== p && {
                borderColor: "grey.400",
                color: "text.primary",
              }),
            }}
          />
        ))}

        <Chip
          key="CUSTOM"
          label="Custom…"
          color={preset === "CUSTOM" ? "primary" : "default"}
          variant={preset === "CUSTOM" ? "filled" : "outlined"}
          onClick={() => handlePreset("CUSTOM")}
          sx={{
            ...(preset !== "CUSTOM" && {
              borderColor: "grey.400",
              color: "text.primary",
            }),
          }}
        />
      </Paper>

      <Dialog open={customOpen} onClose={() => setCustomOpen(false)}>
        <DialogTitle>Select custom range</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            <TextField
              label="Start"
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End"
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={applyCustom}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
